import React, { useState } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ShieldCheck, Wrench, AlertTriangle, CheckCircle2,
  Loader2, RefreshCw, Info, ChevronDown, ChevronUp,
  Shield, Eye, UserX
} from 'lucide-react';
import { Button } from '../../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

export default function DataIntegrityCheck() {
  const [report, setReport]     = useState(null);
  const [loading, setLoading]   = useState(false);
  const [repairing, setRepairing] = useState(false);
  const [expanded, setExpanded] = useState({ fixes: true, warnings: true });

  // DSGVO Anonymisierung
  const [dsgvoPreview, setDsgvoPreview]     = useState(null);
  const [dsgvoLoading, setDsgvoLoading]     = useState(false);
  const [dsgvoRunning, setDsgvoRunning]     = useState(false);

  const runCheck = async () => {
    setLoading(true);
    setReport(null);
    try {
      const res = await axios.get(`${API}/api/superadmin/data-integrity/check`, { headers: authHeader() });
      setReport(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Fehler beim Check');
    } finally { setLoading(false); }
  };

  const runRepair = async () => {
    setRepairing(true);
    setReport(null);
    try {
      const res = await axios.post(`${API}/api/superadmin/data-integrity/repair`, {}, { headers: authHeader() });
      setReport(res.data);
      if (res.data.total_fixes > 0) {
        toast.success(`${res.data.total_fixes} Problem(e) automatisch repariert!`);
      } else {
        toast.success('Alles in Ordnung – keine Reparaturen nötig');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Fehler bei der Reparatur');
    } finally { setRepairing(false); }
  };

  const runDsgvoPreview = async () => {
    setDsgvoLoading(true);
    setDsgvoPreview(null);
    try {
      const res = await axios.get(`${API}/api/superadmin/dsgvo/anonymization-preview`, { headers: authHeader() });
      setDsgvoPreview(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Fehler');
    } finally { setDsgvoLoading(false); }
  };

  const runDsgvoAnonymize = async () => {
    if (!window.confirm('DSGVO-Anonymisierung starten? Personenbezogene Daten werden unwiderruflich anonymisiert.')) return;
    setDsgvoRunning(true);
    try {
      const res = await axios.post(`${API}/api/superadmin/dsgvo/anonymize`, {}, { headers: authHeader() });
      setDsgvoPreview(res.data);
      if (res.data.total > 0) {
        toast.success(`${res.data.total} Kunde(n) DSGVO-konform anonymisiert`);
      } else {
        toast.success('Keine Kunden zur Anonymisierung fällig');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Fehler');
    } finally { setDsgvoRunning(false); }
  };

  const allOk = report && report.total_fixes === 0 && report.total_warnings === 0;
  const hasIssues = report && (report.total_fixes > 0 || report.total_warnings > 0);

  return (
    <DashboardLayout>
      <div className="p-4 sm:p-6 max-w-2xl mx-auto pb-10 space-y-5">

        {/* ── Header ── */}
        <div>
          <div className="flex items-center gap-2 mb-1">
            <ShieldCheck className="h-6 w-6 text-primary" />
            <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Datenqualität
            </h1>
          </div>
          <p className="text-sm text-muted-foreground">
            Prüft und repariert automatisch häufige Dateninkonsistenzen in der Datenbank.
          </p>
        </div>

        {/* ── Aktions-Buttons ── */}
        <div className="grid grid-cols-2 gap-3">
          <Button
            variant="outline"
            onClick={runCheck}
            disabled={loading || repairing}
            className="flex items-center justify-center gap-2 h-12 text-sm"
            data-testid="check-btn"
          >
            {loading
              ? <Loader2 className="h-4 w-4 animate-spin" />
              : <RefreshCw className="h-4 w-4" />}
            Nur prüfen
          </Button>
          <Button
            onClick={runRepair}
            disabled={loading || repairing}
            className="flex items-center justify-center gap-2 h-12 text-sm"
            data-testid="repair-btn"
          >
            {repairing
              ? <Loader2 className="h-4 w-4 animate-spin" />
              : <Wrench className="h-4 w-4" />}
            Reparieren
          </Button>
        </div>

        {/* ── Lade-Zustand ── */}
        {(loading || repairing) && (
          <div className="flex items-center justify-center gap-3 py-10 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span className="text-sm">{loading ? 'Prüfe Datenbank…' : 'Repariere Daten…'}</span>
          </div>
        )}

        {/* ── Ergebnis-Banner ── */}
        {report && !loading && !repairing && (
          <div className={`flex items-start gap-3 p-4 rounded-xl border ${
            allOk
              ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-700'
              : report.total_fixes > 0
              ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700'
              : 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-700'
          }`}>
            <div className="flex-shrink-0 mt-0.5">
              {allOk
                ? <CheckCircle2 className="h-5 w-5 text-green-600" />
                : report.total_fixes > 0
                ? <Wrench className="h-5 w-5 text-blue-600" />
                : <AlertTriangle className="h-5 w-5 text-amber-600" />}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm">
                {allOk
                  ? 'Alles in Ordnung – keine Probleme gefunden'
                  : report.total_fixes > 0
                  ? `${report.total_fixes} Problem(e) automatisch repariert`
                  : `${report.total_warnings} Warnung(en) – manuelle Prüfung nötig`}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {new Date(report.finished_at).toLocaleString('de-DE', {
                  day: '2-digit', month: '2-digit', year: 'numeric',
                  hour: '2-digit', minute: '2-digit'
                })}
              </p>
            </div>
          </div>
        )}

        {/* ── Reparierte Probleme ── */}
        {report?.fixes?.length > 0 && (
          <div className="bg-white dark:bg-gray-900 border border-blue-200 dark:border-blue-800 rounded-xl overflow-hidden">
            <button
              onClick={() => setExpanded(e => ({ ...e, fixes: !e.fixes }))}
              className="w-full flex items-center justify-between px-4 py-3 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
            >
              <div className="flex items-center gap-2">
                <Wrench className="h-4 w-4 text-blue-600" />
                <span className="font-semibold text-sm text-blue-800 dark:text-blue-300">
                  Repariert ({report.fixes.length})
                </span>
              </div>
              {expanded.fixes
                ? <ChevronUp className="h-4 w-4 text-blue-500" />
                : <ChevronDown className="h-4 w-4 text-blue-500" />}
            </button>
            {expanded.fixes && (
              <div className="divide-y divide-gray-100 dark:divide-gray-800">
                {report.fixes.map((fix, i) => (
                  <div key={i} className="flex items-start gap-3 px-4 py-3">
                    <CheckCircle2 className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 dark:text-gray-200 leading-snug">
                        {fix.description}
                      </p>
                      <p className="text-xs text-gray-400 font-mono mt-0.5">{fix.type}</p>
                    </div>
                    <span className="text-sm font-bold text-blue-600 dark:text-blue-400 flex-shrink-0">
                      {fix.count}×
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Warnungen ── */}
        {report?.warnings?.length > 0 && (
          <div className="bg-white dark:bg-gray-900 border border-amber-200 dark:border-amber-700 rounded-xl overflow-hidden">
            <button
              onClick={() => setExpanded(e => ({ ...e, warnings: !e.warnings }))}
              className="w-full flex items-center justify-between px-4 py-3 bg-amber-50 dark:bg-amber-900/20 hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors"
            >
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <span className="font-semibold text-sm text-amber-800 dark:text-amber-300">
                  Manuelle Prüfung nötig ({report.warnings.length})
                </span>
              </div>
              {expanded.warnings
                ? <ChevronUp className="h-4 w-4 text-amber-500" />
                : <ChevronDown className="h-4 w-4 text-amber-500" />}
            </button>
            {expanded.warnings && (
              <div className="divide-y divide-gray-100 dark:divide-gray-800">
                {report.warnings.map((w, i) => (
                  <div key={i} className="flex items-start gap-3 px-4 py-3">
                    <Info className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 dark:text-gray-200 leading-snug">
                        {w.description}
                      </p>
                      <p className="text-xs text-gray-400 font-mono mt-0.5">{w.type}</p>
                      {/* Details wenn vorhanden */}
                      {w.details?.length > 0 && (
                        <ul className="mt-2 space-y-1">
                          {w.details.map((d, di) => (
                            <li key={di} className="text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 px-2 py-1 rounded">
                              {d}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                    <span className="text-sm font-bold text-amber-600 dark:text-amber-400 flex-shrink-0">
                      {w.count}×
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Info-Box (kein Report vorhanden) ── */}
        {!report && !loading && !repairing && (
          <div className="p-4 bg-gray-50 dark:bg-gray-800/40 rounded-xl border border-gray-200 dark:border-gray-700 space-y-3">
            <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">Was wird geprüft:</p>
            <ul className="space-y-2 text-sm text-muted-foreground">
              {[
                'Personalbögen ohne Mitarbeiter-Verknüpfung (employee_id)',
                'Verträge ohne Mitarbeiter-Zuordnung (employee_id)',
                'Mitarbeiter & Stationsleitungen ohne Standort-Zuweisung',
                'Doppelte Push-Subscriptions (>3 pro User)',
                'MHD-Produkte ohne Kundenzuordnung (customer_id)',
                'Accounts ohne customer_id (Warnhinweis)',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-2">
                  <ShieldCheck className="h-3.5 w-3.5 text-primary mt-0.5 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
            <p className="text-xs text-muted-foreground pt-2 border-t border-gray-200 dark:border-gray-700">
              "Nur prüfen" zeigt Probleme ohne Änderungen. "Reparieren" behebt alles automatisch was möglich ist.
            </p>
          </div>
        )}

        {/* ── DSGVO Anonymisierungs-Sektion ── */}
        <div className="border-t border-gray-200 dark:border-gray-700 pt-5 space-y-4">
          <div className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-blue-600" />
            <h2 className="text-base font-bold text-gray-800 dark:text-white">DSGVO Anonymisierung</h2>
          </div>
          <p className="text-sm text-muted-foreground">
            Kunden die seit <strong>mehr als 3 Jahren</strong> gelöscht sind, werden nach Art. 17 DSGVO anonymisiert.
            Rechnungs- und Buchhaltungsdaten bleiben gemäß §257 HGB erhalten.
          </p>

          <div className="grid grid-cols-2 gap-3">
            <Button variant="outline" onClick={runDsgvoPreview} disabled={dsgvoLoading || dsgvoRunning}
              className="flex items-center justify-center gap-2 h-11 text-sm">
              {dsgvoLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
              Vorschau
            </Button>
            <Button onClick={runDsgvoAnonymize} disabled={dsgvoLoading || dsgvoRunning}
              className="flex items-center justify-center gap-2 h-11 text-sm bg-blue-600 hover:bg-blue-700">
              {dsgvoRunning ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserX className="h-4 w-4" />}
              Anonymisieren
            </Button>
          </div>

          {dsgvoPreview && (
            <div className={`rounded-xl border p-4 space-y-3 ${
              dsgvoPreview.total === 0
                ? 'bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-700'
                : 'bg-blue-50 dark:bg-blue-900/10 border-blue-200 dark:border-blue-700'
            }`}>
              <div className="flex items-center gap-2">
                {dsgvoPreview.total === 0
                  ? <CheckCircle2 className="h-4 w-4 text-green-600" />
                  : <UserX className="h-4 w-4 text-blue-600" />}
                <p className="text-sm font-semibold">
                  {dsgvoPreview.dry_run ? '[Vorschau] ' : ''}{dsgvoPreview.total === 0
                    ? 'Keine Kunden zur Anonymisierung fällig'
                    : `${dsgvoPreview.total} Kunde(n) ${dsgvoPreview.dry_run ? 'würden' : 'wurden'} anonymisiert`}
                </p>
              </div>
              <p className="text-xs text-muted-foreground">Stichtag: Älter als {new Date(dsgvoPreview.cutoff_date).toLocaleDateString('de-DE')}</p>
              {dsgvoPreview.anonymized?.length > 0 && (
                <ul className="space-y-1">
                  {dsgvoPreview.anonymized.map((a, i) => (
                    <li key={i} className="text-xs text-blue-700 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/20 px-2 py-1 rounded">
                      {a.company_name} · gelöscht am {a.canceled_at ? new Date(a.canceled_at).toLocaleDateString('de-DE') : '–'}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          <div className="text-xs text-muted-foreground bg-gray-50 dark:bg-gray-800/40 rounded-lg p-3 space-y-1">
            <p><strong>Was anonymisiert wird:</strong> Name, E-Mail, Telefon, Personalbögen, Vertrags-Signaturen, Push-Subscriptions</p>
            <p><strong>Was bleibt (§257 HGB, 10 Jahre):</strong> Rechnungsbeträge, Subscription-Daten, anonymisierter Firmenname</p>
          </div>
        </div>

      </div>
    </DashboardLayout>
  );
}
